import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  BadgeCheck,
  MapPin,
  Calendar,
  School,
  Ruler,
  Scale,
  Mail,
  ChevronRight,
  Timer,
  User,
  Share2,
  Download,
  Copy,
  Check,
  Zap,
  Target,
  Activity,
  BarChart3,
  TrendingUp,
  Award,
  Eye,
  MessageCircle,
  FileText,
  ArrowLeft,
  LogIn,
  ExternalLink,
  Play,
  Globe,
  Video
} from "lucide-react";
import { AdminEditPanel, AdminMetricsEditor, AdminGradesEditor } from "../components/AdminEditPanel";
import { AIScoutingSummary, AICollegeProjection, AIPlayerComparison } from "../components/AIAnalysis";
import { PlayerFilesGallery } from "../components/PlayerFileUpload";
import { VideoShowcase } from "../components/VideoShowcase";

import { NSBVerifiedBadge, NSBVerifiedInline, getVerificationLevel, VERIFICATION_TIERS } from "../components/NSBVerifiedBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo as NSBLogoBase } from "../components/NSBLogo";
const NSBLogo = ({ className = "h-10" }) => (
  <div className="flex items-center gap-3">
    <NSBLogoBase className={className} light={true} />
    <div className="hidden sm:block">
      <span className="text-sm font-bold text-white">NATIONAL</span>
      <span className="block text-xs text-white/50 -mt-1">SCOUTING BUREAU</span>
    </div>
  </div>
);

// Grade Color Helper
const getGradeInfo = (grade) => {
  if (!grade) return { label: "N/A", color: "bg-white/10 text-white/60", textColor: "text-white/50" };
  if (grade >= 80) return { label: "Elite", color: "bg-green-500 text-white", textColor: "text-green-600" };
  if (grade >= 70) return { label: "Plus-Plus", color: "bg-green-400 text-white", textColor: "text-green-500" };
  if (grade >= 60) return { label: "Plus", color: "bg-blue-500 text-white", textColor: "text-blue-600" };
  if (grade >= 55) return { label: "Above Avg", color: "bg-blue-400 text-white", textColor: "text-blue-500" };
  if (grade >= 50) return { label: "Average", color: "bg-yellow-400 text-white", textColor: "text-yellow-600" };
  if (grade >= 45) return { label: "Below Avg", color: "bg-orange-400 text-white", textColor: "text-orange-500" };
  return { label: "Developing", color: "bg-red-400 text-white", textColor: "text-red-500" };
};

// Key Metric Circle Component
const MetricCircle = ({ value, label, suffix = "", color = "bg-red-600", verified = false }) => (
  <div className="flex flex-col items-center">
    <div className="relative">
      <div className={`w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 rounded-full ${color} flex items-center justify-center shadow-lg`}>
        <div className="text-center">
          <span className="text-base xs:text-lg sm:text-2xl font-black text-white leading-none">{value || "--"}</span>
          {suffix && <span className="text-[8px] xs:text-[10px] sm:text-xs text-white/80 block">{suffix}</span>}
        </div>
      </div>
      {verified && value && (
        <div className="absolute -top-1 -right-1">
          <NSBVerifiedInline verified={true} />
        </div>
      )}
    </div>
    <span className="text-[10px] sm:text-xs text-white/60 mt-1 sm:mt-2 font-medium text-center leading-tight">{label}</span>
  </div>
);

// Tool Grade Bar Component
const ToolGradeBar = ({ label, grade, icon: Icon, verified = false }) => {
  const gradeInfo = getGradeInfo(grade);
  const percentage = grade ? Math.min((grade / 80) * 100, 100) : 0;
  
  return (
    <div className="py-2.5 sm:py-3 border-b border-white/10 last:border-0">
      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {Icon && <Icon size={14} className="text-white/40 flex-shrink-0" />}
          <span className="text-xs sm:text-sm font-medium text-white/70 truncate">{label}</span>
          {verified && grade && <NSBVerifiedInline verified={true} />}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 ml-2">
          <span className="text-sm sm:text-lg font-bold text-white">{grade || "--"}</span>
          <span className={`text-[9px] sm:text-xs px-1 sm:px-2 py-0.5 rounded-full font-semibold leading-none ${gradeInfo.color} whitespace-nowrap`}>
            {gradeInfo.label}
          </span>
        </div>
      </div>
      <div className="h-1.5 sm:h-2 bg-white/10 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 transition-all duration-1000"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Pitch Data Table Row
const PitchRow = ({ pitch, data }) => (
  <tr className="border-b border-white/10 hover:bg-white/5">
    <td className="py-2 px-3">
      <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${
        pitch === "4-Seam FB" ? "bg-red-500 text-white" :
        pitch === "Slider" ? "bg-blue-500 text-white" :
        pitch === "Curveball" ? "bg-orange-500 text-white" :
        pitch === "Changeup" ? "bg-green-500 text-white" :
        "bg-gray-400 text-white"
      }`}>
        {pitch}
      </span>
    </td>
    <td className="py-2 px-3 text-center font-semibold text-white/70">{data.count || "--"}</td>
    <td className="py-2 px-3 text-center text-white/70">{data.usage || "--"}%</td>
    <td className="py-2 px-3 text-center font-bold text-red-400">{data.avgVelo || "--"}</td>
    <td className="py-2 px-3 text-center text-white/70">{data.maxVelo || "--"}</td>
    <td className="py-2 px-3 text-center text-white/70">{data.avgSpin || "--"}</td>
    <td className="py-2 px-3 text-center text-white/70">{data.whiff || "--"}%</td>
  </tr>
);

export default function PublicAthletePage() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [user, setUser] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);

  useEffect(() => {
    fetchProfile();
    checkAuth();
    fetchAllPlayers();
  }, [athleteId]); // eslint-disable-line

  const fetchAllPlayers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/athletes`);
      if (res.ok) {
        const data = await res.json();
        // API returns {athletes: [...], total, limit, offset}
        setAllPlayers(data.athletes || []);
      }
    } catch (err) {}
  };

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setUser(await res.json());
        }
      }
    } catch (err) {
      console.error("Auth check failed:", err);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await fetch(`${API_URL}/api/public/athlete/${athleteId}`, {
        credentials: "include",
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        
        // Fetch evaluations if user is logged in
        if (token) {
          const evalRes = await fetch(`${API_URL}/api/scout/component-evaluations/${athleteId}`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}` }
          });
          if (evalRes.ok) {
            const evalData = await evalRes.json();
            setEvaluations(evalData.evaluations || []);
          }
        }
      } else if (res.status === 404) {
        setError("Athlete not found");
      } else if (res.status === 403) {
        setError("This profile is not public");
      } else {
        setError("Failed to load profile");
      }
    } catch (err) {
      setError("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Profile link copied!");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white/5 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-900 border-t-red-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading scouting report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white/5 overflow-x-hidden">
        <header className="bg-white border-b border-white/10 px-4 py-4">
          <div className="max-w-6xl mx-auto">
            <Link to="/">
              <NSBLogo />
            </Link>
          </div>
        </header>
        <div className="max-w-6xl mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
            <User className="h-12 w-12 text-white/40" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">{error}</h1>
          <p className="text-white/50 mb-8">The athlete profile you&apos;re looking for is not available.</p>
          <Link to="/">
            <Button className="bg-blue-900 hover:bg-blue-800 text-white">Return to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Calculate metrics from profile - check both metrics and verified_metrics
  const metrics = profile.metrics || profile.verified_metrics || {};
  const selfMetrics = profile.self_reported_metrics || {};
  const isAdminMetric = (key) => !!(profile.metrics?.[key] || profile.verified_metrics?.[key]);
  
  const latestEval = evaluations[0];
  const toolResults = latestEval?.tool_results || {};
  
  // Merge tool grades from extracted PDF data + evaluations + legacy nsb_grades
  const extractedGrades = profile.tool_grades || {};
  const legacyGrades = profile.nsb_grades || {};
  
  // Best available NSB composite score
  const nsbCompositeScore = latestEval?.nsb_composite_score 
    || extractedGrades?.overall?.current 
    || legacyGrades?.overall 
    || null;
  
  // Build combined tool grades for display
  const displayToolGrades = {};
  // From extracted PDF data (has current/future structure)
  Object.entries(extractedGrades).forEach(([key, val]) => {
    if (key !== "overall") {
      displayToolGrades[key] = typeof val === "object" ? val.current : val;
    }
  });
  // From evaluations (override if exists)
  Object.entries(toolResults).forEach(([key, data]) => {
    if (data?.grade) displayToolGrades[key] = data.grade;
  });
  // From legacy nsb_grades (fallback)
  Object.entries(legacyGrades).forEach(([key, val]) => {
    if (key !== "overall" && !displayToolGrades[key]) displayToolGrades[key] = val;
  });

  // Verification level for progressive badge
  const verifyLevel = getVerificationLevel(metrics, displayToolGrades);

  // Social links helper
  const socialPlatforms = {
    twitter: { icon: "𝕏", label: "Twitter/X", color: "bg-black" },
    instagram: { icon: "IG", label: "Instagram", color: "bg-gradient-to-r from-purple-500 to-pink-500" },
    youtube: { icon: "YT", label: "YouTube", color: "bg-red-600" },
    hudl: { icon: "H", label: "Hudl", color: "bg-orange-500" },
    perfect_game: { icon: "PG", label: "Perfect Game", color: "bg-blue-600" },
    prep_baseball: { icon: "PB", label: "Prep Baseball", color: "bg-green-700" },
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white" data-testid="public-athlete-page">
      {/* Header */}
      <header className="bg-black/80 border-b border-white/10 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/">
              <NSBLogo />
            </Link>
            <div className="hidden md:flex items-center gap-2 text-sm text-white/40">
              <ChevronRight size={16} />
              <span>Scouting Report</span>
              <ChevronRight size={16} />
              <span className="text-white font-medium truncate">{profile.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShare}
              className="border-white/20 text-white hover:bg-white/10 hidden sm:flex"
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
              {copied ? "Copied!" : "Share"}
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleShare}
              className="border-white/20 text-white hover:bg-white/10 sm:hidden h-8 w-8"
            >
              {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            </Button>
            {user ? (
              <span className="text-xs sm:text-sm text-white/50 hidden sm:inline">
                Viewing as <span className="font-semibold capitalize text-white/80">{user.role}</span>
              </span>
            ) : (
              <Link to="/login">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm">
                  <LogIn className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              </Link>
            )}
            {user?.role === "admin" && (
              <AdminEditPanel profile={profile} athleteId={athleteId} onSave={fetchProfile} />
            )}
          </div>
        </div>
      </header>

      {/* Verified Badge Hero Section */}
      <div className="bg-gradient-to-r from-[#0a0e27] via-[#0f1535] to-[#0a0e27] py-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            {/* Badge */}
            <NSBVerifiedBadge percent={verifyLevel.percent} size="lg" />

            {/* Status */}
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-black tracking-tight">
                {verifyLevel.percent >= 100 ? (
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-red-400">NSB FULLY VERIFIED</span>
                ) : verifyLevel.percent > 0 ? (
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400/70 via-pink-400/70 to-red-400/70">{verifyLevel.percent}% VERIFIED</span>
                ) : (
                  <span className="text-white/40">EARN YOUR BADGE</span>
                )}
              </h2>
              {verifyLevel.percent < 100 && verifyLevel.nextStep && (
                <p className="text-xs sm:text-sm text-white/40 mt-0.5">
                  Next: <span className="text-amber-400 font-medium">{verifyLevel.nextStep.desc}</span>
                </p>
              )}
              {verifyLevel.percent >= 100 && (
                <p className="text-xs sm:text-sm text-white/40 mt-0.5">All primary metrics verified by NSB scouts with FlightScope technology</p>
              )}
            </div>

            {/* Tier Progress */}
            <div className="flex gap-2 sm:gap-3">
              {verifyLevel.tiers.map((tier) => (
                <div key={tier.key} className="text-center" title={tier.done ? `${tier.label}: Complete` : `${tier.label}: ${tier.desc}`}>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center border ${
                    tier.done
                      ? "bg-gradient-to-br from-purple-500/20 to-red-500/20 border-purple-500/40"
                      : "bg-white/5 border-white/10"
                  }`}>
                    {tier.done ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-purple-400">
                        <path d="M8.5 12.5L11 15L16 10" />
                      </svg>
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-white/20" />
                    )}
                  </div>
                  <span className={`text-[9px] sm:text-[10px] mt-1 block font-medium ${tier.done ? "text-purple-400" : "text-white/30"}`}>
                    {tier.label.split(" ")[0]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Report Header */}
      <div className="bg-gradient-to-r from-blue-900/80 to-blue-800/80 text-white py-6 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-nsb-split rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">NSB</span>
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold">NSB SCOUTING REPORT</h1>
                <p className="text-blue-200 text-xs sm:text-sm">National Scouting Bureau</p>
              </div>
            </div>
            <div className="text-left sm:text-right text-sm text-blue-200">
              <p>Report Generated</p>
              <p className="font-semibold text-white">{new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Left Column - Player Info */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            {/* Player Card */}
            <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 overflow-hidden">
              {/* Player Header with Split Color */}
              <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-red-600 p-6 text-white relative">
                <div className="flex items-start gap-4">
                  {profile.profile_image ? (
                    <img 
                      src={profile.profile_image} 
                      alt={profile.name}
                      className="w-24 h-24 rounded-lg object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-white/20 flex items-center justify-center border-4 border-white shadow-lg">
                      <span className="text-4xl font-black">{profile.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-xl sm:text-2xl font-black truncate">{profile.name}</h2>
                      {profile.is_verified && (
                        <BadgeCheck className="h-6 w-6 text-yellow-400" />
                      )}
                    </div>
                    {profile.jersey_number && (
                      <p className="text-4xl font-black text-white/30 absolute top-4 right-4">#{profile.jersey_number}</p>
                    )}
                    <p className="text-blue-200">{profile.school || "School N/A"}</p>
                    <div className="flex gap-2 mt-3">
                      <span className="px-3 py-1 bg-white text-blue-900 rounded-full text-sm font-bold">
                        {profile.position_primary || "POS"}
                      </span>
                      {profile.position_secondary && (
                        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                          {profile.position_secondary}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Player Details */}
              <div className="p-6 space-y-4">
                <h3 className="text-sm font-bold text-white/50 uppercase tracking-wider">Player Info</h3>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Class of</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{profile.graduation_year || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Age</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{profile.age || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Ruler className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Height</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{profile.height || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Weight</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{profile.weight ? `${profile.weight} lbs` : "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Bats / Throws</p>
                      <p className="font-semibold text-white text-sm sm:text-base">{profile.bats_throws || `${profile.bats || "R"} / ${profile.throws || "R"}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-white/30 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-white/40">Location</p>
                      <p className="font-semibold text-white text-sm sm:text-base truncate">{profile.location || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Button */}
                {profile.contact?.allow_contact && profile.contact?.contact_email && (
                  <a href={`mailto:${profile.contact.contact_email}`} className="block mt-4">
                    <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white">
                      <Mail className="h-4 w-4 mr-2" />
                      Contact Athlete
                    </Button>
                  </a>
                )}

                {/* Social & Video Links */}
                {(profile.social_links || profile.highlight_video_url || profile.video_url) && (
                  <div className="mt-4 space-y-3">
                    <h3 className="text-sm font-bold text-white/50 uppercase tracking-wider">Links</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.highlight_video_url && (
                        <a 
                          href={profile.highlight_video_url.startsWith("http") ? profile.highlight_video_url : `https://${profile.highlight_video_url}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-medium transition-colors"
                          data-testid="highlight-video-link"
                        >
                          <Video size={14} />
                          Highlights
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {profile.video_url && profile.video_url !== profile.highlight_video_url && (
                        <a 
                          href={profile.video_url.startsWith("http") ? profile.video_url : `https://${profile.video_url}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium transition-colors"
                          data-testid="video-link"
                        >
                          <Play size={14} />
                          Video
                          <ExternalLink size={12} />
                        </a>
                      )}
                      {Object.entries(profile.social_links || {}).map(([platform, url]) => {
                        if (!url) return null;
                        const href = url.startsWith("http") ? url : `https://${url}`;
                        const info = socialPlatforms[platform] || { icon: platform.charAt(0).toUpperCase(), label: platform, color: "bg-white/50" };
                        return (
                          <a
                            key={platform}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/10 text-white/70 rounded-lg text-sm font-medium transition-colors"
                            data-testid={`social-link-${platform}`}
                          >
                            <span className={`w-5 h-5 ${info.color} text-white rounded text-[10px] flex items-center justify-center font-bold`}>
                              {info.icon}
                            </span>
                            {info.label}
                            <ExternalLink size={12} />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* NSB Score Card */}
            <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-4 sm:p-6">
              <h3 className="text-sm font-bold text-white/50 uppercase tracking-wider mb-3 sm:mb-4">NSB Composite Score</h3>
              <div className="text-center">
                {nsbCompositeScore ? (
                  <>
                    <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full bg-gradient-to-br from-blue-900 to-red-600 flex items-center justify-center shadow-lg mb-4">
                      <span className="text-4xl sm:text-5xl font-black text-white">{nsbCompositeScore}</span>
                    </div>
                    <p className={`text-lg font-bold ${getGradeInfo(nsbCompositeScore).textColor}`}>
                      {getGradeInfo(nsbCompositeScore).label}
                    </p>
                    <p className="text-sm text-white/50 mt-1">20-80 MLB Scale</p>
                  </>
                ) : (
                  <>
                    <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
                      <span className="text-3xl font-bold text-white/30">--</span>
                    </div>
                    <p className="text-white/50">Not Yet Evaluated</p>
                    <p className="text-sm text-white/30 mt-1">Awaiting scout assessment</p>
                  </>
                )}
              </div>
              {user?.role === "admin" && (
                <AdminGradesEditor profile={profile} athleteId={athleteId} onSave={fetchProfile} />
              )}
            </div>
          </div>

          {/* Right Column - Metrics & Scouting */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Key Metrics */}
            <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-bold text-white">Key Metrics</h3>
                <div className="flex items-center gap-2">
                  {metrics.verified && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold flex items-center gap-1">
                      <BadgeCheck size={14} /> NSB VERIFIED
                    </span>
                  )}
                  {user?.role === "admin" && (
                    <AdminMetricsEditor profile={profile} athleteId={athleteId} onSave={fetchProfile} />
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
                <MetricCircle 
                  value={metrics.fastball_velocity || metrics.exit_velocity} 
                  label={metrics.fastball_velocity ? "Max Velo" : "Exit Velo"}
                  suffix=" mph"
                  color="bg-red-600"
                  verified={isAdminMetric("fastball_velocity") || isAdminMetric("exit_velocity")}
                />
                <MetricCircle 
                  value={metrics.avg_fastball_velocity}
                  label="Avg FB Velo"
                  suffix=" mph"
                  color="bg-blue-900"
                  verified={isAdminMetric("avg_fastball_velocity")}
                />
                <MetricCircle 
                  value={metrics.spin_rate || metrics.max_spin}
                  label="Max Spin"
                  suffix=" rpm"
                  color="bg-gray-700"
                  verified={isAdminMetric("spin_rate") || isAdminMetric("max_spin")}
                />
                <MetricCircle 
                  value={metrics.sixty_time}
                  label="60-Yard"
                  suffix="s"
                  color="bg-green-600"
                  verified={isAdminMetric("sixty_time")}
                />
                <MetricCircle 
                  value={metrics.extension}
                  label="Extension"
                  suffix=" ft"
                  color="bg-blue-900"
                  verified={isAdminMetric("extension")}
                />
              </div>

              {/* Self-Reported Warning */}
              {!metrics.verified && (metrics.sixty_time || metrics.exit_velocity) && (
                <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-sm text-yellow-300">
                    <strong>Self-Reported:</strong> These metrics were provided by the athlete and have not been verified by NSB.
                  </p>
                </div>
              )}
            </div>

            {/* Tool Grades */}
            {Object.keys(displayToolGrades).length > 0 && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg font-bold text-white">Tool Grades (20-80 Scale)</h3>
                  <span className="text-xs sm:text-sm text-white/50">
                    {latestEval ? `Evaluated: ${new Date(latestEval.timestamp).toLocaleDateString()}` : "NSB Grade"}
                  </span>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-6 sm:gap-x-8">
                  {Object.entries(displayToolGrades).map(([tool, grade]) => (
                    <ToolGradeBar 
                      key={tool}
                      label={tool.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      grade={grade}
                      icon={tool.includes("speed") || tool.includes("run") ? Timer : tool.includes("arm") || tool.includes("velocity") ? Target : Activity}
                      verified={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pitch Arsenal (for pitchers) */}
            {(profile.position_primary?.includes("P") || profile.position_primary === "RHP" || profile.position_primary === "LHP") && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-white mb-4">Pitch Arsenal Breakdown</h3>
                
                {/* Mobile card layout */}
                <div className="sm:hidden space-y-3">
                  {profile.pitch_arsenal?.length > 0 ? (
                    profile.pitch_arsenal.map((p, i) => {
                      const pitchName = p.pitch_type || p.pitch || "Unknown";
                      const pitchColor = pitchName.includes("Fast") || pitchName.includes("4S") ? "border-red-500" :
                        pitchName.includes("Slider") || pitchName.includes("Sweep") ? "border-blue-500" :
                        pitchName.includes("Curve") ? "border-orange-500" :
                        pitchName.includes("Change") ? "border-green-500" : "border-gray-400";
                      return (
                        <div key={i} className={`border-l-4 ${pitchColor} bg-white/5 rounded-r-lg p-3`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-sm text-white">{pitchName}</span>
                            {p.usage_pct && <span className="text-xs text-white/50">{p.usage_pct}% usage</span>}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div>
                              <p className="text-xs text-white/50">Avg</p>
                              <p className="font-bold text-sm">{p.avg_velo || "--"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-white/50">Max</p>
                              <p className="font-bold text-sm text-red-600">{p.max_velo || "--"}</p>
                            </div>
                            <div>
                              <p className="text-xs text-white/50">Spin</p>
                              <p className="font-bold text-sm">{p.avg_spin || "--"}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="border-l-4 border-red-500 bg-white/5 rounded-r-lg p-3">
                      <div className="font-bold text-sm text-white mb-2">4-Seam FB</div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div><p className="text-xs text-white/50">Avg</p><p className="font-bold text-sm">{metrics.avg_fastball_velocity || "--"}</p></div>
                        <div><p className="text-xs text-white/50">Max</p><p className="font-bold text-sm text-red-600">{metrics.fastball_velocity || "--"}</p></div>
                        <div><p className="text-xs text-white/50">Spin</p><p className="font-bold text-sm">{metrics.spin_rate || "--"}</p></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Desktop table layout */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-white/10 text-left">
                        <th className="py-2 px-3 font-semibold text-white/60">Pitch</th>
                        <th className="py-2 px-3 text-center font-semibold text-white/60">Usage%</th>
                        <th className="py-2 px-3 text-center font-semibold text-white/60">Avg Velo</th>
                        <th className="py-2 px-3 text-center font-semibold text-white/60">Max Velo</th>
                        <th className="py-2 px-3 text-center font-semibold text-white/60">Avg Spin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profile.pitch_arsenal?.length > 0 ? (
                        profile.pitch_arsenal.map((p, i) => (
                          <tr key={i} className="border-b border-white/10 hover:bg-white/5">
                            <td className="py-2 px-3 font-semibold text-white">{p.pitch_type || p.pitch}</td>
                            <td className="py-2 px-3 text-center">{p.usage_pct ? `${p.usage_pct}%` : "--"}</td>
                            <td className="py-2 px-3 text-center">{p.avg_velo || "--"}</td>
                            <td className="py-2 px-3 text-center font-bold text-red-600">{p.max_velo || "--"}</td>
                            <td className="py-2 px-3 text-center">{p.avg_spin || "--"}</td>
                          </tr>
                        ))
                      ) : (
                        <PitchRow pitch="4-Seam FB" data={{ 
                          usage: metrics.usage_pct_fb || metrics.fastball_usage || "--",
                          avgVelo: metrics.avg_fastball_velocity || metrics.fastball_velocity,
                          maxVelo: metrics.fastball_velocity,
                          avgSpin: metrics.spin_rate,
                          whiff: metrics.whiff_rate
                        }} />
                      )}
                      {metrics.slider_velocity && (
                        <PitchRow pitch="Slider" data={{
                          avgVelo: metrics.slider_velocity,
                          avgSpin: metrics.slider_spin
                        }} />
                      )}
                      {metrics.curveball_velocity && (
                        <PitchRow pitch="Curveball" data={{
                          avgVelo: metrics.curveball_velocity,
                          avgSpin: metrics.curveball_spin
                        }} />
                      )}
                      {metrics.changeup_velocity && (
                        <PitchRow pitch="Changeup" data={{
                          avgVelo: metrics.changeup_velocity
                        }} />
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Scouting Notes */}
            {latestEval?.written_notes && (user?.role === "coach" || user?.role === "scout" || user?.role === "admin") && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FileText size={20} />
                  Scouting Notes
                </h3>
                <div className="prose max-w-none">
                  <p className="text-white/70 whitespace-pre-wrap">{latestEval.written_notes}</p>
                </div>
              </div>
            )}

            {/* Scouting Notes from PDF Report */}
            {profile.scouting_notes && Object.keys(profile.scouting_notes).length > 0 && user && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-4 sm:p-6 overflow-hidden" data-testid="pdf-scouting-notes">
                <h3 className="text-base sm:text-lg font-bold text-white mb-3 sm:mb-4 flex items-center gap-2">
                  <FileText size={18} className="text-blue-900 flex-shrink-0" />
                  NSB Scouting Analysis
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  {Object.entries(profile.scouting_notes).map(([key, value]) => (
                    value && (
                      <div key={key} className="border-l-4 border-blue-900 pl-3 sm:pl-4">
                        <h4 className="text-xs sm:text-sm font-bold text-white/60 uppercase tracking-wider mb-1">
                          {key.replace(/_/g, " ")}
                        </h4>
                        <p className="text-white/70 text-xs sm:text-sm leading-relaxed break-words whitespace-pre-wrap">{value}</p>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* AI Analysis Section - Visible to coaches, scouts, admins */}
            {user && (user.role === "coach" || user.role === "scout" || user.role === "admin") && (
              <>
                <AIScoutingSummary 
                  athleteId={athleteId} 
                  existingSummary={profile.ai_scouting_summary} 
                />
                <AICollegeProjection 
                  athleteId={athleteId} 
                  existingProjection={profile.ai_projection} 
                />
                <AIPlayerComparison 
                  playerA={profile} 
                  players={allPlayers} 
                />
              </>
            )}

            {/* PDF Scouting Report Download/View */}
            {profile.has_scouting_report && user && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-6" data-testid="scouting-report-pdf-section">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FileText size={20} className="text-red-600" />
                  Official Scouting Report PDF
                </h3>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="w-12 h-14 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs">PDF</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white truncate text-sm sm:text-base">
                      {profile.scouting_report_filename || "Scouting Report"}
                    </p>
                    <p className="text-xs sm:text-sm text-white/50">
                      Uploaded {profile.scouting_report_uploaded_at 
                        ? new Date(profile.scouting_report_uploaded_at).toLocaleDateString() 
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="view-pdf-btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          const res = await fetch(`${API_URL}/api/player/scouting-report/${athleteId}?mode=inline`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (res.ok) {
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            window.open(url, "_blank");
                          } else {
                            toast.error("Failed to load PDF");
                          }
                        } catch (err) {
                          toast.error("View failed: " + err.message);
                        }
                      }}
                      className="border-blue-900 text-blue-900 hover:bg-blue-50"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      data-testid="download-pdf-btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          const res = await fetch(`${API_URL}/api/player/scouting-report/${athleteId}?mode=download`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (res.ok) {
                            const blob = await res.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = profile.scouting_report_filename || "scouting_report.pdf";
                            a.click();
                            window.URL.revokeObjectURL(url);
                            toast.success("PDF downloaded!");
                          } else {
                            toast.error("Failed to download PDF");
                          }
                        } catch (err) {
                          toast.error("Download failed: " + err.message);
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Video Showcase */}
            <VideoShowcase
              athleteId={athleteId}
              canEdit={user?.role === "admin"}
              isAdmin={user?.role === "admin"}
            />

            {/* Player Files Gallery - Documents, Videos, Images */}
            <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-6" data-testid="player-files-section">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-900" />
                Player Files & Media
              </h3>
              <PlayerFilesGallery
                athleteId={athleteId}
                isAdmin={user?.role === "admin"}
              />
            </div>

            {/* Bio */}
            {profile.bio && (
              <div className="rounded-xl shadow-sm border border-white/10 bg-white/5 p-6">
                <h3 className="text-lg font-bold text-white mb-4">About</h3>
                <p className="text-white/70 whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {/* Login Prompt for Non-Users */}
            {!user && (
              <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-xl p-4 sm:p-6 text-white">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Eye size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-bold mb-1">Want to see more?</h3>
                    <p className="text-blue-200 text-xs sm:text-sm">
                      Sign in as a coach or scout to view full scouting reports, tool grades, and evaluation history.
                    </p>
                  </div>
                  <Link to="/login" className="w-full sm:w-auto">
                    <Button className="bg-white text-blue-900 hover:bg-white/10 w-full sm:w-auto">
                      Sign In
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-red-500 rounded flex items-center justify-center">
                <span className="text-white font-bold text-xs">NSB</span>
              </div>
              <div>
                <span className="font-bold text-white">National Scouting Bureau</span>
                <p className="text-white/40 text-sm">Data Powered by FlightScope Strike</p>
              </div>
            </div>
            <p className="text-white/40 text-sm">&copy; {new Date().getFullYear()} National Scouting Bureau. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
